import * as THREE from 'three/webgpu';
import {
    isAbstractSketchEdgeObject,
    isSketchSlotObject,
    type SketchSlotObject,
} from '../../mesh/sketch';

/**
 * 草图槽口移动域服务。
 *
 * 设计说明：
 * 1. Move Entities 的选择展示可以是槽口内部边；
 * 2. 实际移动必须回到槽口整体轮廓域；
 * 3. 该服务只负责槽口移动域解析和槽口参数化平移，不处理普通实体变换。
 */
export class SketchSlotMoveDomainService {
    /**
     * 收集当前选择命中的槽口移动域。
     *
     * 设计说明：
     * 1. PropertyManager 可以继续展示用户命中的内部边或轮廓来源；
     * 2. 但移动阶段必须把这些内部边归一成所属槽口，避免只移动端帽弧或单条直边；
     * 3. 同一个槽口只返回一次，防止一次选择多条槽口边时被重复平移。
     *
     * @param entities 当前选择实体。
     * @returns 去重后的槽口对象列表。
     */
    public collectSelectedSlotMovementDomains(entities: readonly THREE.Object3D[]): SketchSlotObject[] {
        const slotMap = new Map<string, SketchSlotObject>();
        entities.forEach((entity) => {
            const slot = this.resolveSlotMovementDomain(entity);
            if (!slot || slotMap.has(slot.uuid)) {
                return;
            }
            slotMap.set(slot.uuid, slot);
        });
        return Array.from(slotMap.values());
    }

    /**
     * 平移槽口对象。
     *
     * 设计说明：
     * 1. 槽口是封闭轮廓对象，不能把端帽圆弧当普通圆弧单独移动；
     * 2. 直槽口和圆弧槽口分别按自身参数语义平移；
     * 3. 构造中心线是槽口实体成员，随各自的参数平移链路同步移动。
     *
     * @param slot 待平移槽口对象。
     * @param translation 位移向量。
     */
    public translateSlotEntity(slot: SketchSlotObject, translation: THREE.Vector3): void {
        if (slot.isArcSlot()) {
            this.translateArcSlotEntity(slot, translation);
        } else {
            this.translateStraightSlotEntity(slot, translation);
        }
        slot.refreshContourGeometry();
    }

    /**
     * 解析实体所属的槽口移动域。
     *
     * @param entity 当前选择实体。
     * @returns 所属槽口；若实体不属于槽口则返回 null。
     */
    private resolveSlotMovementDomain(entity: THREE.Object3D): SketchSlotObject | null {
        if (isSketchSlotObject(entity)) {
            return entity;
        }

        if (!isAbstractSketchEdgeObject(entity)) {
            return null;
        }

        const closedCollection = Reflect.get(entity, 'closedCollection') as unknown;
        if (isSketchSlotObject(closedCollection)) {
            return closedCollection;
        }

        let current: THREE.Object3D | null = entity.parent;
        while (current) {
            if (isSketchSlotObject(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 按直槽口参数语义平移槽口。
     *
     * 设计说明：
     * 1. 直槽口端帽弧半径来自槽口宽度，不能由普通圆弧端点拖拽语义重算；
     * 2. 纯平移只改变接合点、中心线点和端帽弧定义圆心；
     * 3. 端帽弧半径、起终角和方向保持不变，避免移动后左右圆弧变大。
     *
     * @param slot 直槽口对象。
     * @param translation 位移向量。
     */
    private translateStraightSlotEntity(slot: SketchSlotObject, translation: THREE.Vector3): void {
        if (!slot.centerlineStartPoint || !slot.centerlineEndPoint || !slot.centerPoint) {
            throw new Error('移动实体平移直槽口失败：缺少中心线控制点。');
        }

        const startCapDefinition = slot.startCapArc.getArcDefinition();
        const endCapDefinition = slot.endCapArc.getArcDefinition();

        slot.junctionPoints.p1.syncPositionSilently(slot.junctionPoints.p1.getPosition().add(translation));
        slot.junctionPoints.p2.syncPositionSilently(slot.junctionPoints.p2.getPosition().add(translation));
        slot.junctionPoints.p3.syncPositionSilently(slot.junctionPoints.p3.getPosition().add(translation));
        slot.junctionPoints.p4.syncPositionSilently(slot.junctionPoints.p4.getPosition().add(translation));
        slot.centerlineStartPoint.syncPositionSilently(slot.centerlineStartPoint.getPosition().add(translation));
        slot.centerlineEndPoint.syncPositionSilently(slot.centerlineEndPoint.getPosition().add(translation));
        slot.centerPoint.syncPositionSilently(slot.centerPoint.getPosition().add(translation));
        this.syncSlotArcEndpointsFromJunctions(slot);

        slot.getLines().forEach((line) => {
            line.refreshGeometryOnly();
        });
        slot.constructionCenterline?.refreshGeometryOnly();
        slot.startCapArc.updateArcDefinition({
            ...startCapDefinition,
            centerWorld: startCapDefinition.centerWorld.clone().add(translation),
        });
        slot.endCapArc.updateArcDefinition({
            ...endCapDefinition,
            centerWorld: endCapDefinition.centerWorld.clone().add(translation),
        });
    }

    /**
     * 按圆弧槽口参数语义平移槽口。
     *
     * 设计说明：
     * 1. 圆弧槽口由外弧、内弧和两个端帽弧组成，四条弧都应保持半径与角度不变；
     * 2. 纯平移只改变所有控制点和圆弧定义的圆心；
     * 3. 这样可避免外弧或端帽弧进入普通圆弧端点拖拽语义后重新计算半径。
     *
     * @param slot 圆弧槽口对象。
     * @param translation 位移向量。
     */
    private translateArcSlotEntity(slot: SketchSlotObject, translation: THREE.Vector3): void {
        if (!slot.arcCenterPoint || !slot.centerArcMidpoint) {
            throw new Error('移动实体平移圆弧槽口失败：缺少圆弧槽口控制点。');
        }

        slot.junctionPoints.p1.syncPositionSilently(slot.junctionPoints.p1.getPosition().add(translation));
        slot.junctionPoints.p2.syncPositionSilently(slot.junctionPoints.p2.getPosition().add(translation));
        slot.junctionPoints.p3.syncPositionSilently(slot.junctionPoints.p3.getPosition().add(translation));
        slot.junctionPoints.p4.syncPositionSilently(slot.junctionPoints.p4.getPosition().add(translation));
        slot.arcCenterPoint.syncPositionSilently(slot.arcCenterPoint.getPosition().add(translation));
        slot.centerArcMidpoint.syncPositionSilently(slot.centerArcMidpoint.getPosition().add(translation));
        this.syncSlotArcEndpointsFromJunctions(slot);
        slot.getArcs().forEach((arc) => {
            const definition = arc.getArcDefinition();
            arc.updateArcDefinition({
                ...definition,
                centerWorld: definition.centerWorld.clone().add(translation),
            });
        });
        // 构造中心线弧与槽口弧同心，圆心平移后必须同步平移其定义圆心。
        const centerlineArc = slot.constructionCenterlineArc;
        if (centerlineArc) {
            const definition = centerlineArc.getArcDefinition();
            centerlineArc.updateArcDefinition({
                ...definition,
                centerWorld: definition.centerWorld.clone().add(translation),
            });
        }
    }

    /**
     * 按槽口接合点同步内部圆弧端点。
     *
     * 设计说明：
     * 1. 真实槽口中圆弧端点通常与接合点共用同一对象；
     * 2. 历史数据或测试对象中可能存在“坐标相同但对象不同”的端点；
     * 3. 这里统一按槽口拓扑回写，确保端帽弧和圆弧槽口外/内弧端点不会残留旧坐标。
     *
     * @param slot 槽口对象。
     */
    private syncSlotArcEndpointsFromJunctions(slot: SketchSlotObject): void {
        const {p1, p2, p3, p4} = slot.junctionPoints;
        slot.startCapArc.getStartPoint().syncPositionSilently(p4.getPosition());
        slot.startCapArc.getEndPoint().syncPositionSilently(p1.getPosition());
        slot.endCapArc.getStartPoint().syncPositionSilently(p2.getPosition());
        slot.endCapArc.getEndPoint().syncPositionSilently(p3.getPosition());

        if (!slot.isArcSlot()) {
            return;
        }

        const [outerArc, innerArc] = slot.getArcs();
        outerArc?.getStartPoint().syncPositionSilently(p1.getPosition());
        outerArc?.getEndPoint().syncPositionSilently(p2.getPosition());
        innerArc?.getStartPoint().syncPositionSilently(p3.getPosition());
        innerArc?.getEndPoint().syncPositionSilently(p4.getPosition());
    }
}
